/**
 * MED-781: Availability Requests panel for the coach home dashboard.
 *
 * - Same row data as today (AC23): pending list with the per-card details and
 *   the expandable "Responded (N)" subsection.
 * - AC24: max-height bounded — overflow scrolls inside the panel rather than
 *   expanding the page. This keeps the side-by-side layout balanced with the
 *   Upcoming Sessions panel.
 *
 * Extracted from the legacy CoachDashboard.tsx inline AvailabilityRequestsSection
 * so the dashboard can stay a thin orchestrator (Page → Feature → UI).
 */

import { useState } from 'react';
import {
  Card,
  Group,
  Stack,
  Title,
  Badge,
  ThemeIcon,
  Skeleton,
  Divider,
  UnstyledButton,
  Text,
  Avatar,
  Grid,
  Button,
  Box,
} from '@mantine/core';
import { Inbox, ChevronRight, ChevronDown, Clock } from 'react-feather';
import { EmptyState } from '../../../../components/common';
import type { CoachAvailabilityRequest } from '../../../../services/api/coachPortal';
import { parseDateOnly } from '../../../../utils/dateHelpers';

/*
 * Style guide tokens. Mantine's `dimmed` is #868E96, which fails AA here,
 * and it failed far worse inside the blanket opacity these cards used to
 * carry.
 */
const MEDLEY_TEXT_SECONDARY = '#666666';
const MEDLEY_SURFACE_LIGHT = '#FAFAF8';
const MEDLEY_BORDER = '#E8E6E1';

/*
 * MDF-233: this panel used to cap at 640px with its own inner scrollbar. Once
 * "Previous requests" expanded, the inner box scrolled instead of the page and
 * the live PENDING card was cut down to its footer row while two dead REPLACED
 * cards rendered in full below it. The panel now grows with its content and
 * the page owns the only scrollbar.
 */

interface AvailabilityRequestsPanelProps {
  requests: CoachAvailabilityRequest[];
  isLoading: boolean;
  onReview: (requestId: string) => void;
}

export function AvailabilityRequestsPanel({
  requests,
  isLoading,
  onReview,
}: AvailabilityRequestsPanelProps) {
  // MDF-233: these sections render conditionally rather than through Mantine's
  // <Collapse>. Collapse was already broken here before this change: with
  // `in={true}` it still rendered the content with `display: none`, verified
  // in the running app by reading the component's own React state (both flags
  // true) alongside the DOM (display none, height 0px). The Responded section
  // has therefore never opened. A plain conditional is what the panel needs,
  // and it loses only an animation.
  const [respondedOpen, setRespondedOpen] = useState(false);
  const [closedOpen, setClosedOpen] = useState(false);

  // MDF-233: three buckets, not two.
  //
  // Before this change every non-pending request fell into "Responded", which
  // was already wrong for 'cancelled' and would be wrong again for
  // 'superseded'. A replaced request is not something the coach responded to.
  //
  // 'superseded' rows stay visible on purpose. A coach who was asked about
  // Monday and Tuesday, and is now asked about Monday to Wednesday, needs to
  // see the old ask to know the new one really is different and that nothing
  // was lost. They are shown greyed, collapsed and with no action button.
  const pendingRequests = requests.filter(r => r.status === 'pending');
  const respondedRequests = requests.filter(
    r => r.status === 'confirmed' || r.status === 'declined',
  );
  const closedRequests = requests.filter(
    r => r.status === 'superseded' || r.status === 'cancelled' || r.status === 'expired',
  );

  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="gray" radius="sm">
            <Inbox size={14} />
          </ThemeIcon>
          <Title order={4}>Availability Requests</Title>
          {pendingRequests.length > 0 && (
            <Badge size="sm" variant="filled" color="red" circle>
              {pendingRequests.length}
            </Badge>
          )}
        </Group>
      </Group>

      <Box style={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <>
            {/* MED-1394: row-sized placeholders, so the page does not jump on load. */}
            <Stack gap="xs">
              {Array.from({ length: 2 }, (_, i) => <Skeleton key={i} height={72} radius="sm" />)}
            </Stack>
          </>
        ) : requests.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<Inbox size={24} />}
            title="No availability requests"
            description="When you're invited to participate in a cohort, requests will appear here."
          />
        ) : (
          <Stack gap={0}>
            {pendingRequests.map(request => (
              <AvailabilityRequestCard
                key={request.id}
                request={request}
                onReview={onReview}
              />
            ))}
            {respondedRequests.length > 0 && (
              <>
                {pendingRequests.length > 0 && <Divider my="sm" />}
                <UnstyledButton
                  onClick={() => setRespondedOpen(o => !o)}
                  w="100%"
                  py="xs"
                >
                  <Group gap="xs">
                    {respondedOpen ? (
                      <ChevronDown size={16} color="var(--mantine-color-dimmed)" />
                    ) : (
                      <ChevronRight size={16} color="var(--mantine-color-dimmed)" />
                    )}
                    <Text size="sm" fw={500} c="dimmed">
                      Responded ({respondedRequests.length})
                    </Text>
                  </Group>
                </UnstyledButton>
                {respondedOpen && (
                  <Stack gap={0} mt="xs">
                    {respondedRequests.map(request => (
                      <AvailabilityRequestCard
                        key={request.id}
                        request={request}
                        onReview={onReview}
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}
            {closedRequests.length > 0 && (
              <>
                {(pendingRequests.length > 0 || respondedRequests.length > 0) && (
                  <Divider my="sm" />
                )}
                <UnstyledButton onClick={() => setClosedOpen(o => !o)} w="100%" py="xs">
                  <Group gap="xs">
                    {closedOpen ? (
                      <ChevronDown size={16} color="var(--mantine-color-dimmed)" />
                    ) : (
                      <ChevronRight size={16} color="var(--mantine-color-dimmed)" />
                    )}
                    <Text size="sm" fw={500} c="dimmed">
                      Previous requests ({closedRequests.length})
                    </Text>
                  </Group>
                </UnstyledButton>
                {closedOpen && (
                  <Stack gap={0} mt="xs">
                    {closedRequests.map(request => (
                      <AvailabilityRequestCard
                        key={request.id}
                        request={request}
                        onReview={onReview}
                        replacedBy={
                          request.status === 'superseded'
                            ? findReplacement(request, requests)
                            : undefined
                        }
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        )}
      </Box>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AvailabilityRequestCard — per-row card. Preserves today's behavior (AC23).
// ---------------------------------------------------------------------------

interface AvailabilityRequestCardProps {
  request: CoachAvailabilityRequest;
  onReview: (requestId: string) => void;
  /** MDF-233: the request that directly replaced this one, if any. */
  replacedBy?: CoachAvailabilityRequest;
}

/**
 * MDF-233: the direct successor of a replaced request, which is the earliest
 * request for the same cohort asked after it.
 *
 * A slot count on its own does not let a coach compare one ask against
 * another. Naming the successor and the difference is the smallest thing that
 * makes the greyed card do the job the ticket kept it for.
 */
function findReplacement(
  closed: CoachAvailabilityRequest,
  all: CoachAvailabilityRequest[],
): CoachAvailabilityRequest | undefined {
  const closedAt = new Date(closed.requested_at).getTime();
  return all
    .filter(r => r.cohort_id === closed.cohort_id && r.id !== closed.id)
    .filter(r => new Date(r.requested_at).getTime() > closedAt)
    .sort((a, b) => new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime())[0];
}

/** "which asks for 10 time slots (5 fewer)" */
function describeReplacement(
  closed: CoachAvailabilityRequest,
  replacement: CoachAvailabilityRequest,
): string {
  const was = closed.requested_time_slots?.length ?? 0;
  const now = replacement.requested_time_slots?.length ?? 0;
  const asked = new Date(replacement.requested_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const diff = now - was;
  const delta =
    diff === 0
      ? 'the same number'
      : diff > 0
        ? `${diff} more`
        : `${Math.abs(diff)} fewer`;
  return `Replaced by the request of ${asked}, which asks for ${now} time slot${now === 1 ? '' : 's'} (${delta}).`;
}

/**
 * MDF-233: how one request should read on the dashboard.
 *
 * `actionable` is the important part. A replaced, cancelled or expired request
 * must not offer a button, because acting on it writes a response the cohort
 * manager will never see: every manager-facing screen reads only the newest
 * request. That is the quiet half of MDF-233, and the reason these cards are
 * shown but not clickable.
 */
function describeStatus(request: CoachAvailabilityRequest): {
  label: string;
  color: string;
  variant: 'filled' | 'light';
  actionable: boolean;
  note?: string;
} {
  switch (request.status) {
    case 'pending':
      return request.is_urgent
        ? { label: 'Urgent', color: 'red', variant: 'filled', actionable: true }
        : { label: 'Pending Response', color: 'yellow', variant: 'light', actionable: true };
    case 'confirmed':
    case 'declined':
      return { label: 'Responded', color: 'green', variant: 'light', actionable: true };
    case 'superseded':
      return {
        label: 'Replaced',
        color: 'gray',
        variant: 'light',
        actionable: false,
        note: 'No longer valid. A newer request replaced this one.',
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        color: 'gray',
        variant: 'light',
        actionable: false,
        note: 'This request was cancelled. Nothing is needed from you.',
      };
    case 'expired':
    default:
      return {
        label: 'Expired',
        color: 'gray',
        variant: 'light',
        actionable: false,
        note: 'This request expired before it was answered.',
      };
  }
}

function AvailabilityRequestCard({
  request,
  onReview,
  replacedBy,
}: AvailabilityRequestCardProps) {
  const isPending = request.status === 'pending';
  const status = describeStatus(request);
  const isClosed = !status.actionable;

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      mb="sm"
      /*
        MDF-233: a closed request reads as background context, not as work.
        This used to be a blanket opacity of 0.65, which dropped the note to
        about 2.1:1 and faded the badge that carries the whole meaning. Real
        tokens grey the card and leave every word legible.
      */
      style={
        isClosed
          ? { backgroundColor: MEDLEY_SURFACE_LIGHT, borderColor: MEDLEY_BORDER }
          : undefined
      }
    >
      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <Avatar size="md" radius="xl" color="blue">
            <span style={{ display: 'block', transform: 'translateY(3px)' }}>
              {getPartnerInitials(request.partner_name)}
            </span>
          </Avatar>
          <div>
            <Text fw={600} size="sm">
              {request.partner_name || 'Unknown Partner'}
            </Text>
            <Text size="xs" c="dimmed">
              {request.program_name || request.cohort_name}
            </Text>
          </div>
        </Group>
        <Badge size="sm" variant={status.variant} color={status.color}>
          {status.label}
        </Badge>
      </Group>

      <Grid gutter="xs" mb="sm">
        <Grid.Col span={4}>
          <Text size="xs" c={MEDLEY_TEXT_SECONDARY} tt="uppercase" fw={500}>
            Program Dates
          </Text>
          <Text size="sm" fw={500} c={isClosed ? MEDLEY_TEXT_SECONDARY : undefined}>
            {formatDateRange(request.cohort_start_date, request.cohort_end_date)}
          </Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text size="xs" c={MEDLEY_TEXT_SECONDARY} tt="uppercase" fw={500}>
            Groups
          </Text>
          <Text size="sm" fw={500} c={isClosed ? MEDLEY_TEXT_SECONDARY : undefined}>
            {request.groups_count > 0
              ? `${request.groups_count} group${request.groups_count !== 1 ? 's' : ''}, ${request.session_count} × ${request.session_duration_minutes} min`
              : `${request.session_count} sessions × ${request.session_duration_minutes} min`}
          </Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text size="xs" c={MEDLEY_TEXT_SECONDARY} tt="uppercase" fw={500}>
            Requested Days
          </Text>
          <Text size="sm" fw={500} c={isClosed ? MEDLEY_TEXT_SECONDARY : undefined}>
            {formatRequestedDays(request)}
          </Text>
        </Grid.Col>
      </Grid>

      <Group justify="space-between" align="center">
        {status.note ? (
          <Stack gap={2}>
          <Text size="xs" c={MEDLEY_TEXT_SECONDARY} fs="italic">
            {/*
              MDF-233: the date is what makes two closed cards tellable apart.
              A coach comparing a 15 slot ask against a 10 slot ask needs to
              know which came first.
            */}
            Asked {formatAskedOn(request.requested_at)}. {status.note}
          </Text>
          {replacedBy && (
            <Text size="xs" c={MEDLEY_TEXT_SECONDARY}>
              {describeReplacement(request, replacedBy)}
            </Text>
          )}
          </Stack>
        ) : request.response_deadline && isPending ? (
          <Group gap="xs">
            <Clock
              size={14}
              color={
                request.is_urgent ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-gray-6)'
              }
            />
            <Text
              size="xs"
              c={request.is_urgent ? 'red' : 'dimmed'}
              fw={request.is_urgent ? 500 : 400}
            >
              {getResponseDueText(request.response_deadline)}
              {request.reminder_count ? ` · reminded ${request.reminder_count}x` : ''}
            </Text>
          </Group>
        ) : (
          <div />
        )}
        {status.actionable && (
          <Button size="xs" variant="filled" color="dark" onClick={() => onReview(request.id)}>
            {isPending ? 'Review & Submit Availability' : 'Review & Update Availability'}
          </Button>
        )}
      </Group>
    </Card>
  );
}

/** "Sep 15" — the day the CM sent this ask. */
function formatAskedOn(requestedAt: string): string {
  return new Date(requestedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// MED-1383: cohort dates are date-only. A raw new Date() reads them as UTC midnight and
// shows the day before for a coach west of UTC, so parse them as calendar dates.
function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate) return 'TBD';
  const start = parseDateOnly(startDate) ?? new Date(startDate);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  if (!endDate) return start.toLocaleDateString('en-US', opts);
  const end = parseDateOnly(endDate) ?? new Date(endDate);
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  if (startMonth === endMonth && start.getFullYear() === end.getFullYear()) {
    return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
  }
  return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', opts)}`;
}

function formatRequestedDays(request: CoachAvailabilityRequest): string {
  const slotCount = request.requested_time_slots?.length || 0;
  if (slotCount === 0) return 'TBD';
  return `${slotCount} time slot${slotCount !== 1 ? 's' : ''}`;
}

function getResponseDueText(deadline: string): string {
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diffDays = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'Overdue';
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  const formatted = deadlineDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Response due in ${diffDays} days (${formatted})`;
}

function getPartnerInitials(name: string | null): string {
  if (!name) return '??';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
